import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useCallback,
} from 'react';

import { useQuery } from '@apollo/client';
import { Map }      from 'immutable';

import Container        from '@mui/material/Container';
import Grid             from '@mui/material/Grid';
import Typography       from '@mui/material/Typography';
import Box              from '@mui/material/Box';

import { CANDIDATE_APPLICATION_QUERY_JLP } from 'queries/candidate/applicant';

import Display     from 'components/Generic/Display';
import Answers     from 'components/LandingPage/Answers';
import DotProgress from 'components/Generic/DotProgress';

import recordJS      from 'records';
import ApolloWrapper from './ApolloWrapper';

function LandingPageCandidate({
  uri, navigate, theme, campaign, company
}) {
  const [state, setState] = useState(new Map());

  const candidate = state.get('candidate', new Map({
    campaignId: `${campaign.id}`,
    firstName:  '',
    lastName:   '',
    email:      '',
    source:     '',
  }));

  const done      = state.get('done', false);
  const refetched = state.get('refetched', false);
  const reloading = state.get('reloading', false);

  const {
    loading, error, data, refetch,
  } = useQuery(CANDIDATE_APPLICATION_QUERY_JLP);

  const onDone = useCallback(() => {
    navigate(`${uri}/completed`);
  });

  const checkFollowups = useCallback(() => {
    setState(state.set('refetched', false).set('reloading', true));
    refetch();
  });

  useEffect(() => {
    setState(state.set('refetched', true).set('reloading', false));
  }, [data]);

  if (loading || error || !data) return null;

  const record    = recordJS({ data });
  const applicant = record.getIn(['data', 'candidate', 'applications', 'collection', 0]);

  let unanswered = 0;

  applicant.responses.filter((response) => response.getIn(['prompt', 'typeName']) == 'Question').forEach((response) => {
    if (
      (response.get('value') && response.get('value').length > 0)
      || (response.get('url') && response.get('url').length > 0)
      || (response.get('docs') && response.get('docs').size > 0)
    ) return;
    unanswered += 1;
  });

  if (applicant.folder.get('name') != 'pending' && unanswered == 0) {
    onDone();
  }

  return (
    <Container sx={{ position: 'relative' }}>
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{
            mt:        4,
            mb:        6,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontWeight: 'medium',
              fontSize:   '1.5rem',
              color:      'primary.main',
            }}
          >
            {campaign.title}
          </Typography>
        </Grid>

        <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid item lg={8} md={12} xs={12}>
            <Answers
              beDone={() => onDone()}
              checkFollowups={() => checkFollowups()}
              application={applicant}
              isLongForm={campaign.isLongForm()}
              allowLandingPageResponseResubmit={campaign.isOnboarding() || company.allowLandingPageResponseResubmit()}
              isAlwaysSubmittable={campaign.isOnboarding()}
              theme={theme}
              key={`key-${refetched}`}
              refetched={refetched}
              country={campaign.countryShortCode}
            />
          </Grid>
        </Grid>
      </Grid>

      <Display if={reloading}>
        <Grid
          container
          sx={{
            position: 'absolute',
            top:      0,
            left:     0,
            width:    '100%',
            height:   '100vh',
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              textAlign:       'center',
              backgroundColor: '#fff',
              position:        'relative',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontWeight: 'medium',
                fontSize:   '1.5rem',
                color:      'primary.main',
                my:         10,
              }}
            >
              {campaign.title}
            </Typography>

            <Box sx={{ mx: 'auto', display: 'block' }}>
              <DotProgress />

              <Typography
                variant="subtitle"
                component="p"
                sx={{
                  fontWeight: 'bold',
                  fontSize:   '1.5rem',
                  mt:         4,
                  mb:         2,

                  [theme.breakpoints.down('sm')]: {
                    fontSize: '1.2rem',
                  },
                }}
              >
                {t('eos.landing_page.text.thank_you')}
                !
              </Typography>
              <Typography
                variant="subtitle"
                component="p"
                sx={{
                  fontWeight: 'bold',
                  fontSize:   '1.2rem',
                  maxWidth:   '300px',
                  display:    'inline-block',
                  lineHeight: '1.2',

                  [theme.breakpoints.down('sm')]: {
                    fontSize: '1rem',
                  },
                }}
              >
                {t('eos.landing_page.text.processing_answers')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Display>
    </Container>
  );
}

function LandingPageCandidateApollo({
  token, theme, uri, navigate, campaign, company, children,
}) {
  return (
    <ApolloWrapper token={token}>
      <LandingPageCandidate
        theme={theme}
        uri={uri}
        navigate={navigate}
        campaign={campaign}
        company={company}
      />
    </ApolloWrapper>
  );
}

LandingPageCandidateApollo.defaultProps = {
  token: undefined,
};

LandingPageCandidateApollo.propTypes = {
  token: PropTypes.string,
};

export default LandingPageCandidateApollo;
